import React from 'react';

import { Page, useCareplusRouter } from '../../router/context';
import { AppointmentConfirmationTemplate } from '../../../../templates/AppointmentConfirmationTemplate';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { useSpecialist } from '../../store/Specialist';
import { useUnits } from '../../store/Units';
import { ScheduleCard } from './components/ScheduleCard';
import { useDateTime } from '../../store/Datetime';
import { useSpecialties } from '../../store/Specialties';
import { ATTENDANCE_TYPE, useAttendance } from '../../store/Attendance';
import { usePatientContext } from '../../../../contexts/PatientContext';
import { ModalSystemUnavailable } from '../../components/ModalSystemUnavailable';
import useOnCloseSchedule from '../../../../hooks/useOnCloseSchedule';
import { useSchedule } from '../../store/Schedule';
import { Appointments, saveReschedule, saveSchedule } from '../../../../modules/appointmentBook';
import { SystemUnavailableError } from '../../../../errors';
import { SlotUnavailableError } from '../../../../errors/SlotUnavailableError';
import { useAppointments } from '../../store/Appointment';

export function AppointmentConfirmationList() {
  const router = useCareplusRouter();

  const { specialistSelected } = useSpecialist();
  const { specialtySelected } = useSpecialties();
  const { selectedAttendance } = useAttendance();
  const { selectedUnit } = useUnits();
  const { selectedDate } = useDateTime();
  const { selectedAppointment } = useAppointments();

  const { onCloseSchedule } = useOnCloseSchedule({});

  const { patientSelected } = usePatientContext();

  const { schedule: scheduleStore } = useSchedule();

  const [loading, setLoading] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);

  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);

  const [scheduleProps, setScheduleProps] = React.useState<ScheduleCard>();

  const handleError = (error: Error) => {
    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);

    if (error instanceof SlotUnavailableError) setIsModalSlotUnavailableVisible(true);
  };

  const saveSchedules = async () => {
    setLoading(true);

    try {
      let result;

      if (specialistSelected.id) result = await saveSchedule(scheduleStore);

      if (selectedAppointment?.id) {
        result = await saveReschedule(selectedAppointment.id, {
          date: String(selectedAppointment?.selectedDate),
          hour: String(selectedAppointment?.selectedHour)
        });
      }

      router.navigate(Page.CONCLUSION);

      setLoading(false);
    } catch (error: any) {
      handleError(error);

      setLoading(false);
    }
  };

  const handleScheduleProps = () => {
    const specialistUnitSelected = specialistSelected?.units.find(unit => unit.id === selectedUnit);

    const schedule = {} as ScheduleCard;

    schedule.specialistName = specialistSelected?.name ?? '';
    schedule.unitAddress = specialistUnitSelected?.full_address ?? '';
    schedule.unitName = specialistUnitSelected?.name ?? '';
    schedule.patientName = patientSelected?.name ?? '';
    schedule.dateTime = selectedDate || new Date();
    schedule.specialties = specialtySelected?.name ?? '';
    schedule.isTelemedicine = selectedAttendance === ATTENDANCE_TYPE.TELEMEDICINE;

    setScheduleProps(schedule);
  };

  const handleREscheduleProps = (appointment: Appointments) => {
    const schedule = {} as ScheduleCard;

    schedule.specialistName = appointment.resource.name;
    schedule.unitAddress = appointment.unit.address;
    schedule.unitName = appointment.unit.name;
    schedule.patientName = `${appointment.patient.firstName} ${appointment.patient.lastName}`;
    schedule.dateTime = new Date(appointment.date);
    schedule.specialties = appointment.resource.specialties?.[0]?.name || '';
    schedule.isTelemedicine = appointment.service.attendanceType.id === ATTENDANCE_TYPE.TELEMEDICINE;

    setScheduleProps(schedule);
  };

  React.useEffect(() => {
    if (specialistSelected.id) handleScheduleProps();
    if (selectedAppointment?.id) handleREscheduleProps(selectedAppointment);
  }, [specialistSelected, selectedAppointment]);

  return (
    <>
      <AppointmentConfirmationTemplate handleContinue={saveSchedules} loading={loading} goBack={() => router.goBack()}>
        {scheduleProps && <ScheduleCard {...scheduleProps} />}
      </AppointmentConfirmationTemplate>

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />

      <ModalSystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
        callToSupport={() => (location.href = 'https://wa.me/11964079032?whatsapp=11964079032')}
        goToHome={onCloseSchedule}
      />
    </>
  );
}
